import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ConfigProvider, Space, Tag, Tooltip } from 'antd';
import { ArrowLeftOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ThemeProvider } from 'styled-components';
import DateRangePicker from '../../../commonComponents/DateRangePicker';
import ErrorState from '../../../commonComponents/ErrorState';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import {
  CREATOR_STATUS_COLORS,
  DEFAULT_TAB_SLUG,
  EMPTY_STATES,
  TERMS_VERSIONING_NOTICE,
  getStatusColor,
  tabRoute,
} from '../constants';
import { apiErrorStatus } from '../utils/apiError';
import { formatAllowance, resolveCurrentTerms } from '../utils/terms';
import {
  FilterGroup,
  FilterLabel,
  FilterRow,
  HeaderLeft,
  HeaderSection,
  InlineNotice,
  PageSubtitle,
  PageTitle,
  PageWrapper,
  SectionCard,
} from '../Styles';
import FilterSelect from '../components/FilterSelect';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import { useStoreOptions } from '../hooks/useSelectOptions';
import { useCreator, useCreatorPerformance } from '../hooks/useCreatorQueries';
import CreatorEditDrawer from '../CreatorEditDrawer';
import TermsHistoryTable from '../TermsTab/TermsHistoryTable';
import CustomerMixCard from './CustomerMixCard';
import SummaryCards from './SummaryCards';
import BreakdownTable from './BreakdownTable';
import LinksTable from './LinksTable';

/**
 * Creator detail — `/creator-program/creator/:id`.
 *
 * Fetches the profile (§2.3, for terms history and the status/prefix actions) and the
 * performance report (§3.2, for every number on the page). The range and store live in
 * the performance query key, so changing either refetches — the docs are explicit that
 * this response isn't client-paginated or cached and that one call already carries
 * everything the screen needs.
 */
const CreatorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const { dateRange, setDateRange, apiRange } = useDateRangeFilter();
  const [storeId, setStoreId] = useState<string | undefined>();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { options: storeOptions } = useStoreOptions();

  const performanceParams = useMemo(
    () => ({ ...apiRange, store: storeId }),
    [apiRange, storeId]
  );

  const {
    data: creator,
    isLoading: creatorLoading,
    isError: creatorIsError,
    error: creatorError,
    refetch: refetchCreator,
  } = useCreator(id);

  const {
    data: performance,
    isLoading: performanceLoading,
    isFetching: performanceFetching,
    isError: performanceIsError,
    refetch: refetchPerformance,
  } = useCreatorPerformance(id, performanceParams);

  const currentTerms = useMemo(() => resolveCurrentTerms(creator?.terms), [creator]);
  const summary = performance?.summary;
  const metricsLoading = performanceLoading || performanceFetching;

  const themeConfig = useMemo(
    () => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }),
    [isDarkMode]
  );
  const antdConfig = useMemo(() => getAntdThemeConfig(isDarkMode), [isDarkMode]);

  const goBack = useCallback(() => navigate(tabRoute(DEFAULT_TAB_SLUG)), [navigate]);

  const notFound = apiErrorStatus(creatorError) === 404;

  const content = () => {
    if (creatorIsError) {
      return (
        <ErrorState
          title={notFound ? 'Creator not found' : 'Could not load this creator'}
          message={
            notFound
              ? 'This creator does not exist, or does not belong to your merchant account.'
              : 'Please try again.'
          }
          retryText={notFound ? 'Back to creators' : 'Retry'}
          onRetry={notFound ? goBack : () => refetchCreator()}
        />
      );
    }

    return (
      <>
        <FilterRow style={{ marginBottom: 20 }}>
          <FilterGroup>
            <FilterLabel>Date range</FilterLabel>
            <DateRangePicker
              selectedDateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
          </FilterGroup>

          <FilterSelect
            label="Store"
            placeholder="All stores"
            options={storeOptions}
            value={storeId}
            onChange={setStoreId}
          />
        </FilterRow>

        {performanceIsError ? (
          <ErrorState
            title="Could not load performance"
            message="Please try again."
            onRetry={() => refetchPerformance()}
          />
        ) : (
          <>
            {/* The headline metric leads the page, ahead of the money figures. */}
            <CustomerMixCard customers={summary?.customers} loading={metricsLoading} />

            <SummaryCards summary={summary} loading={metricsLoading} />

            <BreakdownTable
              title="By Store"
              variant="store"
              rows={performance?.byStore}
              loading={metricsLoading}
              emptyText={EMPTY_STATES.BY_STORE}
            />

            <BreakdownTable
              title="By Code"
              variant="code"
              rows={performance?.byCode}
              loading={metricsLoading}
              emptyText={EMPTY_STATES.BY_CODE}
            />

            <LinksTable links={performance?.links} loading={metricsLoading} />
          </>
        )}

        <SectionCard>
          <InlineNotice>{TERMS_VERSIONING_NOTICE}</InlineNotice>
          <TermsHistoryTable
            terms={creator?.terms}
            currentTermsId={currentTerms?.id}
            loading={creatorLoading}
          />
        </SectionCard>
      </>
    );
  };

  return (
    <ThemeProvider theme={themeConfig}>
      <ConfigProvider theme={antdConfig}>
        <PageWrapper>
          <HeaderSection>
            <HeaderLeft>
              <Button type="text" icon={<ArrowLeftOutlined />} onClick={goBack}>
                Back to creators
              </Button>
              <PageTitle>
                {creator?.name || 'Creator'}{' '}
                {creator && (
                  <Tag color={getStatusColor(creator.status, CREATOR_STATUS_COLORS)}>
                    {creator.status}
                  </Tag>
                )}
              </PageTitle>
              <PageSubtitle>
                {creator?.handle}
                {creator?.codePrefix ? ` · prefix ${creator.codePrefix}` : ''}
                {currentTerms ? ` · ${formatAllowance(currentTerms)} allowance` : ''}
                {creator && !currentTerms && (
                  <Tooltip title="A creator needs active terms before they can create any discount code.">
                    <Tag color="warning" style={{ marginLeft: 8 }}>
                      No active terms <InfoCircleOutlined />
                    </Tag>
                  </Tooltip>
                )}
              </PageSubtitle>
            </HeaderLeft>

            {creator && (
              <Space>
                <Button icon={<EditOutlined />} onClick={() => setIsEditOpen(true)}>
                  Edit
                </Button>
              </Space>
            )}
          </HeaderSection>

          {content()}

          <CreatorEditDrawer
            open={isEditOpen}
            creator={creator ?? null}
            onClose={() => setIsEditOpen(false)}
          />
        </PageWrapper>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default CreatorDetail;
